import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_ALLOW_LIST_MEMBERS_RESOURCE } from 'src/features/parkingManagement/api/AllowListAPI/useListAllowListMembers';
import { useUpdateAllowListMember } from 'src/features/parkingManagement/api/AllowListAPI/useUpdateAllowListMember';
import AllowListMemberFormModal from 'src/features/parkingManagement/components/AllowLists/forms/AllowListMemberFormModal';
import { allowListMessages } from 'src/features/parkingManagement/utils/allowListMessages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { queryClient } from 'src/lib/react-query';
import { type DataTableNewEntryProps } from 'src/registry/components/table/types';
import { toast } from 'src/registry/hooks/UseToast';
import { generalMessages } from 'src/utils/messages';

import type { AllowListMember } from 'src/features/parkingManagement/types/allowList';

interface AllowListFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMember: AllowListMember;
    children?: React.ReactNode;
}

export default function UpdateAllowListMemberFormModal<TData>({
    className,
    semantics,
    children,
    selectedMember,
}: AllowListFormModalProps<TData>) {
    const { parkingId = '', listId = '' } = useParams();
    const { formatMessage } = useIntl();
    const { mutate, isPending } = useUpdateAllowListMember({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOW_LIST_MEMBERS_RESOURCE] });
            toast({
                title: formatMessage(allowListMessages.updateMemberSuccess),
                description: formatMessage(allowListMessages.updateMemberSuccessDescription, {
                    entry: name,
                }),
            });
        },
    });

    return (
        <AllowListMemberFormModal
            selectedMember={selectedMember}
            className={className}
            mutate={(values, onSuccess) => {
                mutate({ parkingId, listId, memberId: selectedMember.id, ...values }, { onSuccess });
            }}
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(allowListMessages.editMemberSubtitle, {
                entry: selectedMember.name,
            })}
        >
            {children}
        </AllowListMemberFormModal>
    );
}
